import React, { useState, useEffect } from 'react';
import { FiX, FiCpu, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';
import toast from 'react-hot-toast';

const AiMemoryLogModal = ({ isOpen, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0 });

    const fetchMemory = async () => {
        setLoading(true);
        try {
            // Consultar sales_records donde aiAnalyzed es true
            const salesRef = collection(db, 'sales_records');
            const q = query(
                salesRef,
                where('aiAnalyzed', '==', true),
                orderBy('date', 'desc'), // Ordenar por fecha de venta
                limit(100) // Limitar a los últimos 100 para rendimiento
            );

            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            setLogs(data);

            // Calcular estadísticas
            const valid = data.filter(r => r.isValid !== false).length;
            setStats({
                total: data.length,
                valid,
                invalid: data.length - valid
            });

        } catch (error) {
            console.error("Error fetching AI memory:", error);
            toast.error("Error cargando memoria IA");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) fetchMemory();
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-gray-900 text-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col font-mono relative overflow-hidden ring-1 ring-white/10">

                {/* Background Decor */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none"></div>

                {/* Header */}
                <div className="p-6 border-b border-white/10 flex justify-between items-center bg-black/20">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                            <FiCpu className="text-green-400" size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white tracking-wider uppercase">Memoria de IA</h3>
                            <p className="text-gray-400 text-xs">Registro de decisiones autónomas</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Stats Bar */}
                <div className="grid grid-cols-3 divide-x divide-white/10 border-b border-white/10 bg-black/20">
                    <div className="p-4 text-center">
                        <div className="text-2xl font-bold text-white">{stats.total}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-widest">Casos Analizados</div>
                    </div>
                    <div className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-400">{stats.valid}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-widest">Aprobados</div>
                    </div>
                    <div className="p-4 text-center">
                        <div className="text-2xl font-bold text-red-400">{stats.invalid}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-widest">Rechazados</div>
                    </div>
                </div>

                {/* Content List */}
                <div className="flex-1 overflow-y-auto p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-full text-gray-500 gap-2">
                            <span className="animate-pulse">⚡ Consultando red neuronal...</span>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-white/5 sticky top-0 backdrop-blur-md">
                                <tr>
                                    <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Fecha</th>
                                    <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Alumno / Caso</th>
                                    <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Veredicto</th>
                                    <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-1/2">Razonamiento (CoT)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {logs.map((log) => {
                                    const isValid = log.isValid !== false;
                                    return (
                                        <tr key={log.id} className="hover:bg-white/5 transition-colors group">
                                            <td className="p-4 text-sm text-gray-400 whitespace-nowrap align-top">
                                                {log.date}
                                            </td>
                                            <td className="p-4 align-top">
                                                <div className="font-bold text-white">{log.studentName}</div>
                                                <div className="text-xs text-gray-500">{log.course}</div>
                                                {log.evidenceUrl && (
                                                    <a href={log.evidenceUrl} target="_blank" rel="noreferrer" className="text-blue-400 text-xs hover:underline mt-1 block">Ver Evidencia ↗</a>
                                                )}
                                            </td>
                                            <td className="p-4 align-top">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${isValid
                                                        ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                                        : 'bg-red-500/10 text-red-400 border-red-500/20'
                                                    }`}>
                                                    {isValid ? <><FiCheckCircle /> APROBADO</> : <><FiXCircle /> RECHAZADO</>}
                                                </span>
                                            </td>
                                            <td className="p-4 text-sm text-gray-300 leading-relaxed align-top">
                                                <div className="bg-black/30 p-3 rounded-lg border border-white/5 font-sans">
                                                    "{log.aiReason || 'Sin razonamiento registrado'}"
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AiMemoryLogModal;
